import { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import './ContactPage.css';
import { db } from '../../firebase';

import { API_BASE_URL } from '../../constants';

const initialState = {
  name: '',
  email: '',
  message: '',
};

const resolveContactEndpoint = () => {
  return `${API_BASE_URL}/api/contact`;
};

function ContactPage() {
  const [formData, setFormData] = useState(initialState);
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (status !== 'idle') {
      setStatus('idle');
      setErrorMessage('');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    console.log('Contact form submission started', { formData });

    // Validate form data
    if (!formData.name || !formData.email || !formData.message) {
      setErrorMessage('Please fill in all fields.');
      setStatus('error');
      return;
    }

    // Prefer backend contact endpoint; fallback to Firestore when backend is unavailable.
    try {
      try {
        const endpoint = resolveContactEndpoint();
        console.log('Attempting to send email via backend:', endpoint);
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        if (response.ok) {
          setFormData(initialState);
          setStatus('success');
          return;
        }

        console.warn('Backend contact endpoint returned non-OK status:', response.status);
      } catch {
        console.warn('Backend contact endpoint unavailable, falling back to Firestore.');
      }

      console.log('Attempting to save to Firestore...');
      const emailsRef = collection(db, 'emails');
      const docData = {
        name: formData.name,
        email: formData.email,
        content: formData.message,
        createdAt: serverTimestamp(),
      };

      console.log('Document data to save:', docData);
      const docRef = await addDoc(emailsRef, docData);
      console.log('✓ Successfully saved to Firestore with ID:', docRef.id);

      setFormData(initialState);
      setStatus('success');
    } catch (firestoreError) {
      console.error('Failed to save contact message to Firestore:', firestoreError);
      console.error('Error code:', firestoreError.code);
      console.error('Error message:', firestoreError.message);
      
      let errorMsg = 'Could not send your message. ';
      if (firestoreError.code === 'permission-denied') {
        errorMsg += 'Permission denied. Please make sure you have the required permissions.';
      } else if (firestoreError.code === 'unavailable') {
        errorMsg += 'Service temporarily unavailable. Please try again.';
      } else {
        errorMsg += 'Please check your connection and try again.';
      }
      
      setErrorMessage(errorMsg);
      setStatus('error');
    }
  };

  return (
    <div className="contact-shell">
      <header className="contact-hero">
        <div className="layout-container contact-hero__inner">
          <div className="contact-hero__content">
            <span className="contact-label">Contact Origami</span>
            <h1>We would love to learn about your next automation project.</h1>
            <p>
              Share the details of your robotics workflow, integration goals, or partnership ideas. Our team reads every
              message and responds within two business days.
            </p>
          </div>

          <form className="contact-form" onSubmit={handleSubmit} noValidate>
            <div className="contact-form__field">
              <label htmlFor="contact-name">Name</label>
              <input
                id="contact-name"
                name="name"
                type="text"
                autoComplete="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Jane Doe"
              />
            </div>

            <div className="contact-form__field">
              <label htmlFor="contact-email">Email</label>
              <input
                id="contact-email"
                name="email"
                type="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="you@company.com"
              />
            </div>

            <div className="contact-form__field contact-form__field--full">
              <label htmlFor="contact-message">What can we help you with?</label>
              <textarea
                id="contact-message"
                name="message"
                rows={6}
                value={formData.message}
                onChange={handleChange}
                required
                placeholder="Describe your project, timeline, and any materials you would like to share."
              />
            </div>

            <div className="contact-form__actions">
              <button
                type="submit"
                className="contact-form__submit"
                disabled={status === 'loading'}
              >
                {status === 'loading' ? 'Sending...' : 'Send message'}
              </button>
              {status === 'success' && (
                <p className="contact-form__confirmation" role="status">
                  Thank you for reaching out. Someone from Origami will respond shortly.
                </p>
              )}
              {status === 'error' && (
                <p className="contact-form__error" role="status">
                  {errorMessage}
                </p>
              )}
            </div>
          </form>
        </div>
      </header>
    </div>
  );
}

export default ContactPage;
